import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ToolPage, { inputCls, labelCls, primaryBtn, secondaryBtn } from "@/components/ToolPage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionLabel } from "@/components/Eyebrow";

const PURPOSE_LABEL = { prereq_for_major: "Prereq", ge: "GE", elective_strategic: "Strategic elective", elective_other: "Elective" };
const PURPOSE_COLOR = { prereq_for_major: "text-brass", ge: "text-ivy", elective_strategic: "text-ink", elective_other: "text-stone" };

export default function SemesterPlanner() {
  const { user } = useAuth();
  const [stage, setStage] = useState("form");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    current_college: "Valencia College", credits_completed: 12, intended_major: "Mechanical Engineering",
    target_school: "UF", start_semester: "Spring", start_year: 2026, preferred_course_load: 12,
  });
  const [email, setEmail] = useState(""); const [name, setName] = useState("");
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const next = (e) => { e.preventDefault(); setStage(user ? "running" : "gate"); if (user) run(); };

  const run = async (e) => {
    if (e) e.preventDefault();
    if (!user && (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) { toast.error("Please enter a valid email."); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        credits_completed: parseInt(form.credits_completed, 10) || 0,
        start_year: parseInt(form.start_year, 10) || 2026,
        preferred_course_load: parseInt(form.preferred_course_load, 10) || 12,
        email: email || undefined, name: name || undefined,
      };
      const res = await api.post("/tools/semester-planner", payload);
      setResult(res.data); setStage("result");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tool run failed.");
      if (err?.response?.status === 429) setStage("form");
    } finally { setLoading(false); }
  };

  return (
    <ToolPage
      numeral="III"
      name="Semester Planner"
      blurb="Auto-sequences a 4-semester course plan against the Common Prerequisites Manual for your intended major."
      seoTitle="Free Semester Planner for Florida Transfer Students — CPM-Aligned | Beyond Valencia"
      seoDescription="Auto-sequence a 4-semester course plan against Florida's Common Prerequisites Manual for your intended major and target university."
      seoCanonical="/tools/semester-planner"
      seoKeywords="semester planner, Florida CPM, common prerequisites manual, transfer course plan, Valencia course sequencing"
    >
      {stage === "form" && (
        <div className="border border-stone/40 bg-bone p-6 sm:p-10 max-w-3xl">
          <SectionLabel index="A" label="Intake" />
          <form onSubmit={next} className="mt-7 space-y-5" data-testid="planner-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Current college</Label><Input className={inputCls} value={form.current_college} onChange={set("current_college")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Credits completed</Label><Input type="number" className={inputCls} value={form.credits_completed} onChange={set("credits_completed")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Intended major</Label><Input data-testid="planner-major" className={inputCls} value={form.intended_major} onChange={set("intended_major")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Target school</Label><Input className={inputCls} value={form.target_school} onChange={set("target_school")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Start semester</Label>
                <select className={`${inputCls} w-full h-9 px-3 text-sm`} value={form.start_semester} onChange={set("start_semester")}>{["Fall", "Spring", "Summer"].map((s) => (<option key={s} value={s}>{s}</option>))}</select>
              </div>
              <div className="space-y-2"><Label className={labelCls}>Start year</Label><Input type="number" className={inputCls} value={form.start_year} onChange={set("start_year")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Preferred course load (credits/sem)</Label><Input type="number" className={inputCls} value={form.preferred_course_load} onChange={set("preferred_course_load")} required /></div>
            </div>
            <button type="submit" className={primaryBtn} data-testid="planner-next-btn">Continue</button>
          </form>
        </div>
      )}

      {stage === "gate" && (
        <div className="border border-stone/40 bg-bone p-6 sm:p-10 max-w-2xl" data-testid="planner-gate">
          <SectionLabel index="B" label="One step before we run it" />
          <form onSubmit={run} className="mt-7 space-y-5">
            <div className="space-y-2"><Label className={labelCls}>First name</Label><Input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="space-y-2"><Label className={labelCls}>Email</Label><Input type="email" data-testid="planner-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div className="flex gap-3"><button type="submit" disabled={loading} className={primaryBtn} data-testid="planner-submit-btn">{loading ? "Planning…" : "Build my plan"}</button><button type="button" onClick={() => setStage("form")} className={secondaryBtn}>Back</button></div>
          </form>
        </div>
      )}

      {(stage === "running" || loading) && stage !== "result" && (<div className="text-ink-soft">Sequencing semesters against the CPM…</div>)}

      {stage === "result" && result && (
        <div className="space-y-10" data-testid="planner-result">
          {result.semesters?.map((s, i) => (
            <div key={i} className="border border-stone/40 bg-bone p-6">
              <div className="flex items-baseline justify-between border-b border-stone/30 pb-3">
                <h3 className="font-serif text-2xl text-ink tracking-tight">{s.label}</h3>
                <span className="text-[11px] uppercase tracking-[0.22em] text-stone tabular-nums">{s.credits} credits</span>
              </div>
              <table className="w-full mt-4 text-sm">
                <tbody>
                  {s.courses?.map((c, j) => (
                    <tr key={j} className="border-b border-stone/20 last:border-b-0">
                      <td className="py-2.5 text-ink">{c.name}</td>
                      <td className="py-2.5 text-stone text-xs uppercase tracking-[0.18em] tabular-nums">{c.code_pattern}</td>
                      <td className="py-2.5 text-stone tabular-nums text-right">{c.credits}cr</td>
                      <td className={`py-2.5 text-right text-[10px] uppercase tracking-[0.18em] ${PURPOSE_COLOR[c.purpose] || "text-stone"}`}>{PURPOSE_LABEL[c.purpose] || c.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {s.notes && <p className="text-xs text-stone mt-4 italic">{s.notes}</p>}
            </div>
          ))}
          <div className="border border-stone/40 bg-bone-dim p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Prereq completion summary</div>
            <p className="mt-3 text-ink leading-relaxed">{result.prereq_completion_summary}</p>
            {result.risk_flags?.length > 0 && (<><div className="mt-5 text-[10px] uppercase tracking-[0.22em] text-brass">Risk flags</div><ul className="mt-3 space-y-2">{result.risk_flags.map((r, i) => (<li key={i} className="flex gap-2 text-sm text-ink"><span className="text-brass mt-2 h-px w-3 bg-brass flex-shrink-0" /><span>{r}</span></li>))}</ul></>)}
          </div>
          <p className="text-[11px] text-stone italic">{result.disclaimer}</p>
        </div>
      )}
    </ToolPage>
  );
}
